import { DateTime } from 'luxon'

/**
 * Parse an event date as it arrives from the API. The backend serialises dates
 * as epoch millis, but older payloads (and duplicated/edited events held in
 * component state) can carry ISO strings or real Date objects.
 */
export const parseEventDate = (
  value: Date | string | number | null | undefined
): DateTime | null => {
  if (value === null || value === undefined || value === '') return null
  let dt: DateTime
  if (value instanceof Date) dt = DateTime.fromJSDate(value)
  else if (typeof value === 'number') dt = DateTime.fromMillis(value)
  else if (/^\d+$/.test(value)) dt = DateTime.fromMillis(Number(value))
  else dt = DateTime.fromISO(value)
  return dt.isValid ? dt : null
}

/**
 * An event is only past once its final day is over.
 *
 * `endDate` is a DATE column, so it comes back as the *start* of the last day.
 * Comparing it straight against `now` marks an event as past for the whole of
 * the day it is still running, and any timezone skew between the server and the
 * browser pulls that a few hours earlier still. Compare against the end of the
 * end date's day instead, which is also the day the list actually displays.
 */
export const isEventPast = (
  event: { endDate: Date | string | number | null | undefined } | null | undefined,
  now: DateTime = DateTime.now()
): boolean => {
  const end = parseEventDate(event?.endDate)
  if (!end) return false
  return end.endOf('day') < now
}

/** Inverse of {@link isEventPast} — an event that is still running or upcoming. */
export const isEventActive = (
  event: { endDate: Date | string | number | null | undefined } | null | undefined,
  now: DateTime = DateTime.now()
): boolean => !isEventPast(event, now)
